import axios from 'axios';
import { newRequestId } from './requestId';
import supabase from '../supabaseClient/supabaseClient';

export function publishAiUsage(usage, userId) {
  window.dispatchEvent(new CustomEvent('worksheep-ai-usage', { detail: { usage, userId } }));
}
const MAX_RETRIES = 2;
const RETRYABLE_STATUSES = new Set([408, 500, 502, 503, 504]);

function canRetry(error) {
  const code = error.response?.data?.code;
  if (['AI_DAILY_LIMIT', 'REQUEST_CONFLICT', 'REQUEST_EXPIRED'].includes(code)) return false;
  if (error.code === 'ERR_CANCELED') return false;
  if (error.response) {
    return RETRYABLE_STATUSES.has(error.response.status) ||
      (error.response.status === 409 && code === 'REQUEST_IN_PROGRESS') ||
      (error.response.status === 429 && code === 'AI_QUOTA_BUSY');
  }
  return ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error.code);
}

function retryDelay(error, retry) {
  const header = error.response?.headers?.['retry-after'];
  const seconds = Number(header);
  const serverDelay = header == null ? 0 : Number.isFinite(seconds)
    ? seconds * 1000 : Date.parse(header) - Date.now();
  return Math.max(2000 * retry, Number.isFinite(serverDelay) ? serverDelay : 0);
}

export async function sendAiRequest(path, payload, { requestId = newRequestId(), action = 'generate', onRetry = () => {} } = {}) {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session?.access_token) throw new Error('Please sign in before generating a worksheet.');
  // Keep one key across attempts: the server replays completed results and only
  // reserves a fresh attempt after a failed generation has released its slot.
  const config = { headers: {
    Authorization: `Bearer ${session.access_token}`,
    'Idempotency-Key': requestId,
    'X-Generation-Action': action,
  } };
  for (let attempt = 0; ; attempt += 1) {
    let response;
    try {
      response = await axios.post(`${process.env.REACT_APP_BE_URL}${path}`, payload, config);
    } catch (error) {
      const delay = retryDelay(error, attempt + 1);
      // Do not silently wait through a long server-directed backoff.
      if (attempt < MAX_RETRIES && canRetry(error) && delay <= 10000) {
        onRetry({ retry: attempt + 1, maxRetries: MAX_RETRIES });
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      if (error.response?.data?.usage) publishAiUsage(error.response.data.usage, session.user?.id);
      else window.dispatchEvent(new Event('worksheep-ai-refresh'));
      throw error;
    }
    if (response.data.usage) publishAiUsage(response.data.usage, session.user?.id);
    return response;
  }
}
